import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AuthUser, clearStoredAuth, getStoredToken, getStoredUser, loginRequest, logoutRequest, storeAuth, storeUser, type UserRole } from '@/lib/api';

type AuthContextValue = {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, expectedRole?: UserRole) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setUser(getStoredUser());
    setToken(getStoredToken());
    setIsLoading(false);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    token,
    isLoading,
    isAuthenticated: Boolean(user && token),
    async login(email: string, password: string, expectedRole?: UserRole) {
      const { user: loggedUser, token: accessToken } = await loginRequest(email, password);

      if (expectedRole && loggedUser.role !== expectedRole) {
        throw new Error(`Ce compte est de type "${loggedUser.role}" et ne correspond pas au rôle sélectionné.`);
      }

      storeAuth(accessToken, loggedUser);
      setUser(loggedUser);
      setToken(accessToken);
    },
    async logout() {
      try {
        await logoutRequest();
      } catch {
        // Ignore network/logout errors, local session must still be cleared.
      } finally {
        clearStoredAuth();
        setUser(null);
        setToken(null);
      }
    },
    updateUser(updatedUser: AuthUser) {
      storeUser(updatedUser);
      setUser(updatedUser);
    },
  }), [isLoading, token, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
